import type { GoalVsReal } from '../../../hooks/useReports';

interface Props {
  goals: GoalVsReal[];
  loading: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d: string) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }) : null;

export const GoalsVsReality = ({ goals, loading }: Props) => {
  if (loading) return (
    <div className="space-y-3">
      {[1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />)}
    </div>
  );

  if (goals.length === 0) return (
    <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
      <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-2xl">
        <i className="ri-flag-line text-2xl text-gray-300" />
      </div>
      <div>
        <p className="text-[14px] font-600 text-gray-700">Nenhuma meta ativa</p>
        <p className="text-[12px] text-gray-400 mt-0.5">Crie metas na seção de Metas para acompanhar o progresso aqui.</p>
      </div>
    </div>
  );

  const onTrack = goals.filter(g => !g.isAtRisk).length;
  const atRisk  = goals.filter(g => g.isAtRisk).length;

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-center">
          <p className="text-[20px] font-700 text-gray-800">{goals.length}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Metas ativas</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center">
          <p className="text-[20px] font-700 text-emerald-600">{onTrack}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">No ritmo</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-center">
          <p className="text-[20px] font-700 text-amber-600">{atRisk}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Em risco</p>
        </div>
      </div>

      {/* Goals list */}
      <div className="space-y-3">
        {goals.map(goal => {
          const remaining = Math.max(0, goal.targetAmount - goal.currentAmount);
          const pct = Math.round(goal.progress);
          const dateLabel = fmtDate(goal.targetDate ?? '');
          return (
            <div
              key={goal.id}
              className={`bg-white border rounded-2xl p-4 ${goal.isAtRisk ? 'border-amber-200' : 'border-gray-100'}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0" style={{ backgroundColor: goal.color + '20' }}>
                  <i className={`${goal.icon} text-[16px]`} style={{ color: goal.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[13px] font-700 text-gray-800">{goal.name}</p>
                    <div className="flex items-center gap-1.5">
                      {goal.isAtRisk && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-600">Em risco</span>
                      )}
                      <span className="text-[12px] font-700 text-gray-700">{pct}%</span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: goal.color }}
                    />
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-4 text-[11px] text-gray-500 flex-wrap">
                    <span><span className="font-600 text-gray-700">{fmt(goal.currentAmount)}</span> de {fmt(goal.targetAmount)}</span>
                    <span>Restam <span className="font-600 text-gray-700">{fmt(remaining)}</span></span>
                    {goal.monthlyNeeded && (
                      <span className={`${goal.isAtRisk ? 'text-amber-600 font-600' : ''}`}>
                        Mensal necessário: {fmt(goal.monthlyNeeded)}
                      </span>
                    )}
                    {dateLabel && (
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        Prazo: {dateLabel}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
